import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Container,
  useTheme,
  alpha,
  Divider,
  useMediaQuery,
  Avatar,
  Fade,
  Zoom,
} from '@mui/material';
import {
  AccessTime as AccessTimeIcon,
  Person as PersonIcon,
  Spa as SpaIcon,
  Notes as NotesIcon,
  AttachMoney as MoneyIcon,
  CalendarToday as CalendarIcon,
  CheckCircle as CheckCircleIcon,
} from '@mui/icons-material';
import { useParams } from 'react-router-dom';
import { format } from 'date-fns';
import { findCompanyBySlug } from '../../client-experience/utils/companyAccess';
import { getPublicAppointmentOrNull } from '../../api/storefront/storefront';
import { PageSkeleton } from '../../components/loading/skeletons';

const formatPhoneNumber = (phone) => {
  const rawPhone = String(phone || '').trim();
  if (!rawPhone) return '';

  const digits = rawPhone.replace(/\D/g, '');
  const normalizedDigits = digits.length === 11 && digits.startsWith('1') ? digits.slice(1) : digits;

  if (normalizedDigits.length !== 10) {
    return rawPhone;
  }

  return `(${normalizedDigits.slice(0, 3)}) ${normalizedDigits.slice(3, 6)}-${normalizedDigits.slice(6)}`;
};

const parseAppointmentDate = (value) => {
  if (!value) return null;
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
  if (typeof value?.toDate === 'function') return value.toDate();
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const AppointmentDetailsPage = () => {
  const [appointment, setAppointment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [companyName, setCompanyName] = useState('');
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { companySlug, appointmentId } = useParams();

  useEffect(() => {
    const fetchAppointment = async () => {
      try {
        const matchedCompany = await findCompanyBySlug(companySlug);
        if (!matchedCompany?.id) {
          setError('Appointment not found');
          return;
        }
        const appointmentData = await getPublicAppointmentOrNull({
          companyId: matchedCompany.id,
          id: appointmentId,
        });
        if (!appointmentData) {
          setError('Appointment not found');
          return;
        }
        setCompanyName(matchedCompany.name || '');
        setAppointment({
          ...appointmentData,
          dateTime: parseAppointmentDate(appointmentData.dateTime),
          endTime: parseAppointmentDate(appointmentData.endTime),
        });
      } catch (err) {
        console.error('Error fetching appointment:', err);
        setError('Failed to load appointment details');
      } finally {
        setLoading(false);
      }
    };

    if (appointmentId) {
      fetchAppointment();
    }
  }, [appointmentId, companySlug]);

  if (loading) {
    return (
      <Box sx={{ minHeight: '100vh', width: '100%', p: 3 }}>
        <PageSkeleton variant="form" label="Loading appointment" />
      </Box>
    );
  }

  if (error) {
    return (
      <Box 
        sx={{
          minHeight: '100vh',
          width: '100%',
          background: `linear-gradient(180deg, 
            ${alpha(theme.palette.primary.main, 0.1)} 0%, 
            ${alpha(theme.palette.background.paper, 1)} 25%,
            ${alpha(theme.palette.background.paper, 1)} 75%,
            ${alpha(theme.palette.primary.main, 0.1)} 100%
          )`,
          py: { xs: 2, sm: 4 },
        }}
      >
        <Container maxWidth="sm">
          <Zoom in>
            <Paper
              elevation={2}
              sx={{
                p: 4,
                borderRadius: 2,
                bgcolor: 'background.paper',
                boxShadow: theme.shadows[3],
              }}
            >
              <Typography color="error" variant="h6" gutterBottom>
                {error}
              </Typography>
              <Typography color="text.secondary" variant="body2">
                Please check the appointment link and try again.
              </Typography>
            </Paper>
          </Zoom>
        </Container>
      </Box>
    );
  }

  if (!appointment) return null;

  const formattedClientPhone = formatPhoneNumber(appointment.clientPhone);

  const InfoRow = ({ icon, label, value, secondaryValue }) => (
    <Box 
      sx={{ 
        display: 'flex', 
        alignItems: 'flex-start', 
        gap: 2,
        p: 2,
        borderRadius: 1,
        '&:hover': {
          bgcolor: alpha(theme.palette.primary.main, 0.04),
        },
        transition: 'background-color 0.2s ease',
      }}
    >
      {React.cloneElement(icon, { 
        sx: { 
          color: theme.palette.primary.main,
          opacity: 0.8,
        } 
      })}
      <Box>
        <Typography variant="caption" color="text.secondary" display="block">
          {label}
        </Typography>
        <Typography variant="body1">
          {value}
        </Typography>
        {secondaryValue && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {secondaryValue}
          </Typography>
        )}
      </Box>
    </Box>
  );

  return (
    <Box sx={{ 
      minHeight: '100vh',
      width: '100%',
      background: `linear-gradient(180deg, 
        ${alpha(theme.palette.primary.main, 0.1)} 0%, 
        ${alpha(theme.palette.background.paper, 1)} 25%,
        ${alpha(theme.palette.background.paper, 1)} 75%,
        ${alpha(theme.palette.primary.main, 0.1)} 100%
      )`,
      py: { xs: 2, sm: 4 },
      px: { xs: 0, sm: 2 },
    }}>
      <Container maxWidth="md">
        <Zoom in timeout={800}>
          <Paper 
            elevation={2}
            sx={{ 
              overflow: 'hidden',
              borderRadius: { xs: 0, sm: 2 },
              bgcolor: 'background.paper',
              boxShadow: theme.shadows[3],
            }}
          >
            {/* Header Section */}
            <Box 
              sx={{ 
                textAlign: 'center',
                bgcolor: alpha(theme.palette.primary.main, 0.04),
                borderBottom: `1px solid ${theme.palette.divider}`,
                p: { xs: 3, sm: 4 },
                position: 'relative',
              }}
            >
              <Fade in timeout={1200}>
                <Box>
                  <Box
                    sx={{
                      width: 80,
                      height: 80,
                      borderRadius: '50%',
                      bgcolor: alpha(theme.palette.primary.main, 0.1),
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      margin: '0 auto',
                      mb: 3,
                    }}
                  >
                    <CheckCircleIcon 
                      sx={{ 
                        fontSize: 40,
                        color: theme.palette.primary.main,
                      }} 
                    />
                  </Box>
                  <Typography 
                    variant={isMobile ? "h5" : "h4"} 
                    color="primary.main"
                    gutterBottom
                    sx={{ 
                      fontWeight: 600,
                      mb: 1,
                    }}
                  >
                    Appointment Details
                  </Typography>
                  <Typography 
                    variant="subtitle1" 
                    color="text.secondary"
                    sx={{ mb: 1 }}
                  >
                    Booking Reference: #{appointmentId.slice(-6).toUpperCase()}
                  </Typography>
                  {companyName && (
                    <Typography 
                      variant="body2" 
                      color="text.secondary"
                    >
                      {companyName}
                    </Typography>
                  )}
                </Box>
              </Fade>
            </Box>

            {/* Content Section */}
            <Fade in timeout={1600}>
              <Paper 
                elevation={0}
                sx={{ 
                  p: { xs: 2, sm: 3 }, 
                  mb: 3,
                  mx: { xs: 2, sm: 4 },
                  bgcolor: 'background.paper',
                  border: `1px solid ${theme.palette.divider}`,
                  borderRadius: 2,
                }}
              >
                <Typography 
                  variant="h6" 
                  gutterBottom
                  sx={{ 
                    mb: 2,
                    pb: 2,
                    borderBottom: `1px solid ${theme.palette.divider}`,
                  }}
                >
                  Appointment Information
                </Typography>
                
                <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                  <InfoRow
                    icon={<PersonIcon />}
                    label="Customer"
                    value={appointment.clientName}
                    secondaryValue={`${appointment.clientEmail} • ${formattedClientPhone || appointment.clientPhone}`}
                  />
                  <InfoRow
                    icon={<SpaIcon />}
                    label="Service"
                    value={appointment.serviceName}
                    secondaryValue={appointment.service?.description}
                  />
                  <InfoRow
                    icon={<PersonIcon />}
                    label="Staff Member"
                    value={appointment.employeeName}
                  />
                  <InfoRow
                    icon={<CalendarIcon />}
                    label="Date & Time"
                    value={format(appointment.dateTime, 'EEEE, MMMM d, yyyy')}
                    secondaryValue={format(appointment.dateTime, 'h:mm a')}
                  />
                  <InfoRow
                    icon={<AccessTimeIcon />}
                    label="Duration"
                    value={`${appointment.duration} minutes`}
                    secondaryValue={`Ends at ${format(appointment.endTime, 'h:mm a')}`}
                  />

                  {appointment.notes && (
                    <InfoRow
                      icon={<NotesIcon />}
                      label="Notes"
                      value={appointment.notes}
                    />
                  )}

                  {appointment.totalPrice > 0 && (
                    <InfoRow
                      icon={<MoneyIcon />}
                      label="Price"
                      value={`$${appointment.totalPrice}`}
                    />
                  )}
                </Box>
              </Paper>
            </Fade>

            {/* Footer Section */}
            <Fade in timeout={2000}>
              <Box 
                sx={{ 
                  p: { xs: 2, sm: 3 },
                  bgcolor: alpha(theme.palette.primary.main, 0.03),
                  borderTop: `1px solid ${theme.palette.divider}`,
                }}
              >
                <Typography variant="body2" color="text.secondary" align="center">
                  This appointment was booked through Neat Schedule
                </Typography>
              </Box>
            </Fade>
          </Paper>
        </Zoom>
      </Container>
    </Box>
  );
};

export default AppointmentDetailsPage; 